<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';
import { useRoute } from 'vue-router';

import { performanceMark } from '@libs/utils';

import {
  useBrowserStore,
  useCustomerFlowStore,
  usePerformanceStore,
  useRecipeBackupStore,
  useRouterStore,
  useStatsigEventsStore,
} from '../../core/stores';
import { useRecipeStore } from '../../core/stores/recipe/recipeStore';

import HotelSelectionContentDesktop from './components/HotelSelectionContentDesktop.vue';
import HotelSelectionContentMobile from './components/HotelSelectionContentMobile.vue';
import HotelSelectionDesktopLayout from './components/HotelSelectionDesktopLayout.vue';
import HotelSelectionHeaderDesktop from './components/HotelSelectionHeaderDesktop.vue';
import HotelSelectionHeaderMobile from './components/HotelSelectionHeaderMobile.vue';
import HotelSelectionMobileLayout from './components/HotelSelectionMobileLayout.vue';
import HotelSelectionModals from './components/HotelSelectionModals.vue';
import ListMapSwitch from './components/ListMapSwitch.vue';
import { HotelSelectionViewType } from './models';
import { useHotelSelectionStore, useHotelSelectionMapStore } from './stores';

const browserStore = useBrowserStore();
const hotelSelectionStore = useHotelSelectionStore();
const hotelSelectionMapStore = useHotelSelectionMapStore();
const route = useRoute();
const routerStore = useRouterStore();
const recipeStore = useRecipeStore();
const statsigEventsStore = useStatsigEventsStore();
const recipeBackupStore = useRecipeBackupStore();
const performanceStore = usePerformanceStore();
const customerFlowStore = useCustomerFlowStore();

const shouldShowBackButton = ref<boolean>(true);

onMounted(() => {
  setTimeout(() => window.scrollTo(0, 1), 1);
});

onMounted(async () => {
  performanceMark('HotelSelectionViewInitiated');
  performanceStore.trackHotelSelectionPageRenderedPerformance();

  const index = route.params.ingredientIndex;

  shouldShowBackButton.value =
    !customerFlowStore.isHotelOnlyFromMarketplaceFlow ||
    !!routerStore.previousTripPlannerPage;

  if (typeof index === 'string') {
    const indexAsNumber = parseInt(index);

    if (isNaN(indexAsNumber)) {
      const regex = /^h\d+$/;
      if (regex.test(index)) {
        const stayIndex = index.slice(1);
        const stayIndexAsNumber = parseInt(stayIndex);
        hotelSelectionStore.initializeHotelSelectionView(stayIndexAsNumber);
      } else {
        statsigEventsStore.logHotelSelectionWithWrongIndexAccessed(
          window.location.href
        );
        await routerStore.navigateToTripOverview();
      }
    } else {
      const stayOnlyIndex =
        recipeStore.mapRecipeIndexToStayOnlyIndex(indexAsNumber);

      // NOTE: This condition handles situations where someone has old URL: hotel-selection/0,
      // and one hotel in recipe. In this case we want to show hotel selection.
      if (
        stayOnlyIndex === undefined &&
        indexAsNumber === 0 &&
        recipeStore.isSingleHotelRecipe
      ) {
        await routerStore.navigateToHotelSelection({ stayOnlyIndex: 0 });
        hotelSelectionStore.initializeHotelSelectionView(0);
      }
      // NOTE: This condition takes care of other valid old URLs.
      else if (stayOnlyIndex !== undefined) {
        await routerStore.navigateToHotelSelection({ stayOnlyIndex });
        hotelSelectionStore.initializeHotelSelectionView(stayOnlyIndex);
        statsigEventsStore.logHotelSelectionWithOldIndexAccessed(
          window.location.href
        );
      } else {
        // HACK: Navigate here is twice, otherise is does not work,
        // due to redirect from XX number of nights to number of nights fetched from BE.
        // Clean up task: https://trello.com/c/JOYlFnlj/4598-clean-up-after-migration-to-stay-only-indexes-in-hotel-selection
        statsigEventsStore.logHotelSelectionWithWrongIndexAccessed(
          window.location.href
        );
        await routerStore.navigateToTripOverview();
        await routerStore.navigateToTripOverview();
      }
    }
  }
  hotelSelectionMapStore.destroyMap(); // HACK: This is a way to fix zooming out map in hotel selection page https://trello.com/c/DvEarbjd
});

onUnmounted(() => {
  recipeBackupStore.restoreBackup();
  hotelSelectionStore.resetHotelSelectionView();
});

const hasInitializeHotelSelectionList = ref<boolean>(false);

function onInitializeHotelSelectionList(): void {
  if (hasInitializeHotelSelectionList.value) {
    return;
  }
  hotelSelectionStore.handleInitializedEvent();
  hasInitializeHotelSelectionList.value = true;
}

function onBackButtonClick(): void {
  statsigEventsStore.logHotelSelectionBackButtonClicked();
}
</script>

<template>
  <!-- NOTE: Hotel details panel is mounted via child route -->
  <router-view />

  <HotelSelectionModals />

  <HotelSelectionMobileLayout
    v-if="browserStore.isSmallerThan1200px"
    :has-fixed-height="
      hotelSelectionStore.viewType === HotelSelectionViewType.Map
    "
    :content-full-screen="
      hotelSelectionStore.viewType === HotelSelectionViewType.Map
    "
  >
    <template #header>
      <HotelSelectionHeaderMobile
        :back-button-link="routerStore.getTripOverviewUrl()"
        :should-show-back-button="shouldShowBackButton"
        @back="onBackButtonClick"
      />
    </template>
    <template #content>
      <HotelSelectionContentMobile
        @initialized-hotel-selection-list="onInitializeHotelSelectionList()"
      />
    </template>
    <template #list-map-switch>
      <ListMapSwitch
        v-if="hotelSelectionStore.hotelSelectionCards.length > 0"
      />
    </template>
  </HotelSelectionMobileLayout>

  <HotelSelectionDesktopLayout v-else>
    <template #upper-panel>
      <HotelSelectionHeaderDesktop
        :should-show-back-button="shouldShowBackButton"
      />
    </template>
    <template #lower-panel>
      <HotelSelectionContentDesktop
        @initialized-hotel-selection-list="onInitializeHotelSelectionList()"
      />
    </template>
  </HotelSelectionDesktopLayout>
</template>
